/**
 * PageHost — generic React wrapper for hand-built `.html`-style page
 * designs that ship as { STYLES, MARKUP, SCRIPT } string-constant modules.
 *
 * Responsibilities (all mount-scoped, with proper cleanup):
 *
 *   1. Load Source Serif 4 once, app-wide, via a `<link>` injected into
 *      <head>. Idempotent — only adds the link if it's not already there.
 *
 *   2. Compute and inject the scoped stylesheet (CSS prefixed with
 *      `.<hostClass>` via `scopeCSS`) so the file's `:root`, `*`, `body`
 *      and class selectors cannot leak to the rest of the app.
 *
 *   3. Mount the markup into a wrapper `<div class="<hostClass>">` via
 *      `dangerouslySetInnerHTML`. The source is developer-authored static
 *      content (verbatim copy of the user's hand-built HTML file in the
 *      upgrade zip), not user input — there is no XSS surface.
 *
 *   4. Run the file's <script> body in the page context after mount. The
 *      script's `document.querySelector` calls find the elements directly
 *      in the main document, IntersectionObservers fire on the main
 *      window scroll, and `requestAnimationFrame` loops live and die with
 *      the page.
 *
 *   5. Intercept clicks on internal anchor tags (href starts with "/")
 *      and route them through React Router via `window.history.pushState`
 *      + a synthetic `popstate` event. External links and in-page anchors
 *      (`#…`) are left alone, so smooth-scroll behaviour from the file's
 *      CSS `html { scroll-behavior:smooth }` (already global in the app
 *      reset) continues to work. This is disabled when `wireLinks={false}`
 *      — used by the homepage where the hrefs are still placeholders
 *      waiting for the user's route map.
 *
 *   6. Apply page-agnostic patches via `extraPatches` (CSS text, appended
 *      to a single style block alongside the global ones). Page-specific
 *      JS hooks (e.g. monotonic HIW progression) are wired by the parent
 *      component via the `onMount(hostEl)` callback, which returns an
 *      optional teardown function.
 *
 *   7. Fix the font-load race: when fonts are still loading at script
 *      run, in-script width measurements (cycling hero word, etc.) use
 *      the fallback serif and are ~35% too narrow. After
 *      `document.fonts.ready` we dispatch a `resize` event so the file's
 *      own resize handler re-measures.
 */
import { useEffect, useMemo, useRef } from "react";
import { scopeCSS } from "./scopeCSS";
import "../../styles/pagehost-hero-overrides.css";

const SOURCE_SERIF_HREF =
  "https://fonts.googleapis.com/css2?family=Source+Serif+4:ital,wght@0,400;0,500;0,600;1,400;1,500&display=swap";

function loadSourceSerifOnce() {
  if (typeof document === "undefined") return;
  if (document.querySelector('link[data-trex-host-font]')) return;
  const link = document.createElement("link");
  link.rel = "stylesheet";
  link.href = SOURCE_SERIF_HREF;
  link.setAttribute("data-trex-host-font", "");
  document.head.appendChild(link);
}

// Global app-side .reveal collision fix. The app's
// /app/frontend/src/styles/animations.css ships
// `.reveal { opacity:0; transform:translateY(22px); transition: ... }`
// waiting for an IntersectionObserver elsewhere to add `.is-revealed`.
// The hand-built pages use `.reveal` as a static class tag and most never
// wire that observer up, so their headings, subtitles and counters would
// be invisible. We force visibility ONLY inside the scoped host wrapper —
// other pages keep their reveal-on-scroll behaviour intact. Per-page
// hero sections may define their own `.reveal { animation: rise }` which
// wins on specificity (e.g. homepage hero).
function revealCollisionPatch(hostClass) {
  return `
    .${hostClass} .reveal {
      opacity: 1 !important;
      transform: none !important;
      transition: none !important;
    }
    /* Allow the homepage hero animation (the only one that uses .reveal
       with its own keyframe animation) to win — it lives under .hero. */
    .${hostClass} .hero .reveal {
      opacity: initial !important;
      transform: initial !important;
      transition: initial !important;
    }
  `;
}

function topbarHidePatch(hostClass) {
  return `.${hostClass} .topbar { display: none !important; }`;
}

export function PageHost({
  styles,
  markup,
  script,
  hostClass,
  wireLinks = true,
  linkMap = null,
  extraPatches = "",
  onMount,
  "data-testid": testId,
}) {
  const hostRef = useRef(null);
  const scopedCSS = useMemo(
    () => scopeCSS(styles, hostClass),
    [styles, hostClass]
  );

  // Some of the verbatim user-uploaded HTML files ship their own
  // <link rel="stylesheet" href="https://fonts.googleapis.com/css2?…">
  // tag for fonts (Source Serif 4, Inter, JetBrains Mono). Four files
  // (learn / solutions-institutions / solutions-builders / solutions-app-store)
  // request the *variable optical-size* version of Source Serif 4
  // (`opsz,wght@0,8..60,...`), the four others (home / network / ledger /
  // erc-3643) rely on the static-weight version PageHost injects via
  // `loadSourceSerifOnce`. When both are loaded on the same document the
  // browser resolves "Source Serif 4" to the *opsz* variant, which renders
  // visibly narrower glyphs at the hero size — making the affected pages
  // look like they use a different weight/family.
  //
  // We strip any <link> to fonts.googleapis.com / fonts.gstatic.com from
  // the injected markup so the only Source Serif 4 face on the page is
  // the canonical one PageHost loads via `loadSourceSerifOnce`. The
  // verbatim files on disk are not modified.
  const cleanedMarkup = useMemo(
    () =>
      typeof markup === "string"
        ? markup.replace(
            /<link\b[^>]*\bhref=["'][^"']*fonts\.(?:googleapis|gstatic)\.com[^"']*["'][^>]*>/gi,
            ""
          )
        : markup,
    [markup]
  );

  useEffect(() => {
    loadSourceSerifOnce();

    const host = hostRef.current;
    if (!host) return;

    // 1) Patch stylesheet (topbar hide + reveal fix + page-specific patches)
    const patchStyle = document.createElement("style");
    patchStyle.setAttribute("data-trex-host-patch", "");
    patchStyle.textContent =
      topbarHidePatch(hostClass) +
      "\n" +
      revealCollisionPatch(hostClass) +
      "\n" +
      extraPatches;
    document.head.appendChild(patchStyle);

    // 2) Run the file's <script>
    const scriptEl = document.createElement("script");
    scriptEl.setAttribute("data-trex-host-script", "");
    scriptEl.textContent = script;
    document.body.appendChild(scriptEl);

    // 3) Intercept anchor clicks for the right behavior on each href shape:
    //    - "/route"            → SPA navigation via React Router (pushState)
    //    - "#/route"           → SAME as above; some of the upgrade-zip
    //                            pages wrote internal routes with a leading
    //                            hash by mistake. We treat them as routes.
    //    - "#section-id"       → in-page anchor: smooth-scroll to the
    //                            target element. We do this in JS because
    //                            we drop the file's html { scroll-behavior:
    //                            smooth } rule during CSS scoping.
    //    - everything else     → default browser behavior (external links,
    //                            mailto:, tel:, plain "#" with no target).
    //
    //    Optionally, `linkMap` is applied FIRST as a one-time DOM rewrite
    //    on mount, replacing each anchor's href with its mapped target.
    //    That way browser-level affordances (hover-tooltip URL, right-click
    //    "Open in new tab", middle-click) all use the real destination
    //    instead of the placeholder, while the existing click dispatcher
    //    below picks up the correct path/anchor shape on plain clicks.
    if (linkMap) {
      host.querySelectorAll("a[href]").forEach((a) => {
        const h = a.getAttribute("href");
        if (h && Object.prototype.hasOwnProperty.call(linkMap, h)) {
          const target = linkMap[h];
          a.setAttribute("href", target);
          if (/^https?:/i.test(target)) {
            a.setAttribute("target", "_blank");
            a.setAttribute("rel", "noopener noreferrer");
          }
        }
      });
    }

    // Some upgrade-zip pages wrote internal routes as `#/route` (stray
    // leading hash). Rewrite those at mount so the browser shows the real
    // URL on hover, right-click "Open in new tab" works, and middle-click
    // opens the proper route in a new tab. Pure in-page anchors `#section`
    // and externals are left untouched.
    if (wireLinks) {
      host.querySelectorAll('a[href^="#/"]').forEach((a) => {
        const h = a.getAttribute("href");
        if (h && h.startsWith("#/")) a.setAttribute("href", h.slice(1));
      });
    }

    let detachLinkClicks = () => {};
    if (wireLinks) {
      const handler = (e) => {
        if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) return;
        const a = e.target.closest("a[href]");
        if (!a || !host.contains(a)) return;
        let href = a.getAttribute("href");
        if (!href || href === "#") return;
        // protocol / external / mailto / tel: leave alone
        if (/^(https?:)?\/\//i.test(href) || /^(mailto:|tel:|javascript:)/i.test(href)) return;

        // "#/path" → "/path" (internal route written with stray hash)
        if (href.startsWith("#/")) href = href.slice(1);

        // In-page anchor: smooth-scroll to target inside this host
        if (href.startsWith("#") && href.length > 1) {
          const target =
            host.querySelector(href) || document.querySelector(href);
          if (target) {
            e.preventDefault();
            target.scrollIntoView({ behavior: "smooth", block: "start" });
            // also reflect the hash in the URL without triggering history
            // navigation on the outer router
            if (window.history && window.history.replaceState) {
              window.history.replaceState(null, "", href);
            }
          }
          return;
        }

        // Internal app route — SPA navigation via React Router
        if (href.startsWith("/") && !href.startsWith("//")) {
          e.preventDefault();
          window.history.pushState({}, "", href);
          window.dispatchEvent(new PopStateEvent("popstate"));
          window.scrollTo({ top: 0, left: 0, behavior: "auto" });
        }
      };
      host.addEventListener("click", handler);
      detachLinkClicks = () => host.removeEventListener("click", handler);
    }

    // 4) Page-specific hooks
    let onMountCleanup = () => {};
    if (typeof onMount === "function") {
      const ret = onMount(host);
      if (typeof ret === "function") onMountCleanup = ret;
    }

    // 5) Font-load race correction
    let fontsCancelled = false;
    if (document.fonts && document.fonts.ready) {
      document.fonts.ready
        .then(() => {
          if (!fontsCancelled) window.dispatchEvent(new Event("resize"));
        })
        .catch(() => {});
    }

    return () => {
      fontsCancelled = true;
      onMountCleanup && onMountCleanup();
      detachLinkClicks();
      scriptEl.remove();
      patchStyle.remove();
    };
  }, [script, hostClass, wireLinks, extraPatches, linkMap, onMount]);

  return (
    <>
      {/*
        Both `dangerouslySetInnerHTML` usages below are by design:

          1. <style> — receives the scoped CSS string returned by
             `scopeCSS`, which is built from a developer-authored string
             constant (the .styles.js sibling of this host). The string
             never touches user input.

          2. <div class="${hostClass}"> — mounts the verbatim hand-built
             HTML markup the user shipped as a static `.markup.js` module.
             That HTML often contains inline <svg>, <canvas>, custom data
             attributes and css-only animations that DOMPurify would
             strip, defeating the entire "host the user's HTML verbatim"
             architecture this component exists to provide. The MARKUP
             source is a string literal in the codebase, not user input,
             and there is no XSS surface here.
      */}
      <style
        data-trex-host=""
        dangerouslySetInnerHTML={{ __html: scopedCSS }}
      />
      <div
        ref={hostRef}
        className={hostClass}
        data-testid={testId}
        dangerouslySetInnerHTML={{ __html: cleanedMarkup }}
      />
    </>
  );
}
